import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { crmSetupConfigurationService } from "../services/crmSetupConfigurationService";
import type {
  CrmSetupConfigurationOption,
  CrmSetupConfigurationStatus,
  CrmSetupMetaConnectionInput,
  CrmSetupMetaConnectionResult,
} from "../types";
import { useCrmSetupMetaEmbeddedSignup } from "./useCrmSetupMetaEmbeddedSignup";

export interface UseCrmSetupConfigurationResult {
  configurationStatus: CrmSetupConfigurationStatus | null;
  isLoadingConfiguration: boolean;
  isSavingOption: boolean;
  isSavingMetaConnection: boolean;
  isMetaConnected: boolean;
  setConfigurationOption: (
    option: CrmSetupConfigurationOption,
  ) => Promise<void>;
  clearFluxmedOption: () => Promise<void>;
  isRemovingFluxmedOption: boolean;
  connectMetaAccount: () => void | Promise<void>;
  setManualMetaConnection: (
    input: CrmSetupMetaConnectionInput,
  ) => Promise<void>;
  disconnectMetaAccount: () => Promise<void>;
  saveMetaAccountSelection: () => Promise<void>;
  isSavingMetaAccountSelection: boolean;
  resetMetaSignupState: () => void;
  hasMetaEmbeddedSignupConfig: boolean;
  isSecureContextForMeta: boolean;
  isMetaEmbeddedSignupReady: boolean;
  isMetaEmbeddedSignupLoading: boolean;
  metaEmbeddedSignupError: string | null;
}

interface UseCrmSetupConfigurationProps {
  organizationId?: string;
  /** Contrato `pos-atendimento` executado — obrigatório para vincular Meta. */
  hasFluxZapExtensionActive?: boolean;
}

export function useCrmSetupConfiguration(
  props: UseCrmSetupConfigurationProps = {},
): UseCrmSetupConfigurationResult {
  const organizationId = props.organizationId;
  const hasFluxZapExtensionActive = props.hasFluxZapExtensionActive ?? false;
  const [configurationStatus, setConfigurationStatus] =
    useState<CrmSetupConfigurationStatus | null>(null);

  /**
   * Importante:
   * - Ao receber FINISH do Embedded Signup, NÃO persistimos Endpoint automaticamente.
   * - Apenas guardamos o payload em memória (estado do hook) e aguardamos ação explícita do usuário.
   */
  const captureMetaFromEmbeddedSignup = useCallback(
    async (metaConnection: CrmSetupMetaConnectionResult) => {
      if (!hasFluxZapExtensionActive) return;

      setConfigurationStatus(() => ({
        selectedOption: "metaEmbeddedSignup",
        metaConnection,
      }));
    },
    [hasFluxZapExtensionActive],
  );

  const metaSignup = useCrmSetupMetaEmbeddedSignup(
    captureMetaFromEmbeddedSignup,
  );

  const { run: runLoadConfiguration, loading: isLoadingConfiguration } =
    useAsyncTask<[], CrmSetupConfigurationStatus>({
      task: useCallback(async () => {
        const nextStatus = await crmSetupConfigurationService.getStatus({
          organizationId,
        });
        setConfigurationStatus(nextStatus);
        return nextStatus;
      }, [organizationId]),
    });

  const {
    run: runSaveMetaAccountSelection,
    loading: isSavingMetaAccountSelection,
  } = useAsyncTask<[], CrmSetupConfigurationStatus>({
    task: useCallback(async () => {
      const currentMetaConnection = configurationStatus?.metaConnection;
      if (!currentMetaConnection?.wabaId) {
        throw new Error("Conecte a conta Meta antes de salvar.");
      }

      await crmSetupConfigurationService.persistMetaEndpoint(
        currentMetaConnection,
        { organizationId },
      );

      const nextStatus = await crmSetupConfigurationService.getStatus({
        organizationId,
      });
      setConfigurationStatus(nextStatus);
      return nextStatus;
    }, [configurationStatus?.metaConnection, organizationId]),
  });

  const { run: runSetConfigurationOption, loading: isSavingOption } =
    useAsyncTask<[CrmSetupConfigurationOption], void>({
      task: useCallback(
        async (option: CrmSetupConfigurationOption) => {
          setConfigurationStatus((previous) => {
            const next: CrmSetupConfigurationStatus = {
              selectedOption: option,
              metaConnection:
                option === "metaEmbeddedSignup"
                  ? previous?.metaConnection ?? null
                  : null,
            };
            return next;
          });

          // Persist Fluxmed option to the backend so it survives page reloads
          if (option === "fluxmedDefault") {
            await crmSetupConfigurationService.persistFluxmedOption({
              organizationId,
            });
            // Refresh the real backend state
            await runLoadConfiguration();
          }
        },
        [organizationId, runLoadConfiguration],
      ),
    });

  const setConfigurationOption = useCallback(
    async (option: CrmSetupConfigurationOption) => {
      if (option === "metaEmbeddedSignup" && !hasFluxZapExtensionActive) {
        toast.warning(
          "Assine a extensão FluxZap para vincular sua conta WhatsApp via Meta.",
        );
        return;
      }
      await runSetConfigurationOption(option);
    },
    [hasFluxZapExtensionActive, runSetConfigurationOption],
  );

  const disconnectMetaAccount = useCallback(async () => {
    metaSignup.resetResult();
    await crmSetupConfigurationService.clearMetaConnection({ organizationId });
    const nextStatus = await crmSetupConfigurationService.getStatus({
      organizationId,
    });
    setConfigurationStatus(nextStatus);
  }, [metaSignup.resetResult, organizationId]);

  const connectMetaAccount = useCallback(async () => {
    if (!hasFluxZapExtensionActive) {
      toast.warning(
        "Assine a extensão FluxZap para vincular sua conta WhatsApp via Meta.",
      );
      return;
    }

    metaSignup.openEmbeddedSignup();
  }, [hasFluxZapExtensionActive, metaSignup.openEmbeddedSignup]);

  const setManualMetaConnection = useCallback(
    async (input: CrmSetupMetaConnectionInput) => {
      if (!hasFluxZapExtensionActive) {
        toast.warning(
          "Assine a extensão FluxZap para vincular sua conta WhatsApp via Meta.",
        );
        return;
      }

      const wabaId = input.wabaId.trim();
      if (!wabaId) {
        throw new Error("Informe o WABA ID para vinculação manual.");
      }

      setConfigurationStatus(() => ({
        selectedOption: "metaEmbeddedSignup",
        metaConnection: {
          authorizationCode: null,
          wabaId,
          businessId: input.businessId?.trim() || null,
          wabaName: input.wabaName?.trim() || null,
          primaryPhoneNumberId: input.primaryPhoneNumberId?.trim() || null,
          primaryPhoneValue: input.primaryPhoneValue?.trim() || null,
        },
      }));
    },
    [hasFluxZapExtensionActive],
  );

  const clearFluxmedOption = useCallback(async () => {
    setConfigurationStatus((previous) => {
      if (previous?.selectedOption !== "fluxmedDefault") {
        return previous;
      }
      return { selectedOption: null, metaConnection: null };
    });
  }, []);

  const saveMetaAccountSelection = useCallback(async () => {
    if (!hasFluxZapExtensionActive) {
      throw new Error(
        "Assine a extensão FluxZap para salvar a vinculação Meta.",
      );
    }
    await runSaveMetaAccountSelection();
  }, [hasFluxZapExtensionActive, runSaveMetaAccountSelection]);

  const resetMetaSignupState = useCallback(() => {
    metaSignup.resetResult();
  }, [metaSignup.resetResult]);

  useEffect(() => {
    runLoadConfiguration();
  }, [runLoadConfiguration]);

  const hadFluxZapExtensionActiveRef = useRef<boolean | null>(null);
  useEffect(() => {
    const previous = hadFluxZapExtensionActiveRef.current;
    hadFluxZapExtensionActiveRef.current = hasFluxZapExtensionActive;

    if (previous !== true || hasFluxZapExtensionActive) return;
    if (configurationStatus?.selectedOption !== "metaEmbeddedSignup") return;

    void disconnectMetaAccount();
  }, [
    configurationStatus?.selectedOption,
    disconnectMetaAccount,
    hasFluxZapExtensionActive,
  ]);

  const isMetaConnected = useMemo(
    () => Boolean(configurationStatus?.metaConnection?.wabaId),
    [configurationStatus?.metaConnection?.wabaId],
  );

  return {
    configurationStatus,
    isLoadingConfiguration,
    isSavingOption,
    isSavingMetaConnection: false,
    isMetaConnected,
    setConfigurationOption,
    clearFluxmedOption,
    isRemovingFluxmedOption: false,
    connectMetaAccount,
    setManualMetaConnection,
    disconnectMetaAccount,
    saveMetaAccountSelection,
    isSavingMetaAccountSelection,
    resetMetaSignupState,
    hasMetaEmbeddedSignupConfig: metaSignup.hasConfiguration,
    isSecureContextForMeta: metaSignup.isSecureContext,
    isMetaEmbeddedSignupReady: metaSignup.isReady,
    isMetaEmbeddedSignupLoading: metaSignup.isLoading,
    metaEmbeddedSignupError: metaSignup.error,
  };
}
